import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getAsset } from '../../../../../../../../../util/assets';
import Badge from '../../../../../../../../../components/global/Badge';
import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import Button from '../../../../../../../../../components/global/Button';
import AddedToCartLabel from '../../../PhotoPurchasingOptions/AddedToCartLabel';
import {
  CURRENCY,
  getCurrencySymbol,
} from '../../../../../../../../../constants/currency';
import {
  getAlbumCustomPrintName,
  getAlbumCustomPrintPrice,
} from '../../../../../../../../../util/custom-prints';
import ImagePreload from '../../../../../../../../../components/global/ImagePreload';
import { formatPriceForDisplay } from '../../../../../../../../../util/format-money';
import AlbumCustomPrintPreviewModal from '../AlbumCustomPrintOption/AlbumCustomPrintPreviewModal';
import AccountCardAlbumCustomPrintConfiguratorModal from './AccountCardAlbumCustomPrintConfigurator/modal';
import { REQUIRES_PRINT_PACKAGE_ALERT_MODAL } from '../RequiresPrintPackageAlert';
import useOpenModal from '../../../../../../../../../hooks/useOpenModal';

const propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  albumCustomPrint: PropTypes.shape({
    id: PropTypes.string.isRequired,
    price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
    discount_price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
  }).isRequired,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onAlbumCustomPrintToggle: PropTypes.func.isRequired,
  hasPurchasedSubscription: PropTypes.bool,
  hasSubscriptionInCart: PropTypes.bool,
  subscriptionAvailable: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledElement: PropTypes.node,
  hideActions: PropTypes.bool,
  isAllPhotos: PropTypes.bool,
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  teamPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      photo: PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
      }).isRequired,
    }),
  ),
};

const AccountCardAlbumCustomPrintOption = ({
  className,
  readOnly,
  albumCustomPrint,
  identity,
  cartItem,
  onAlbumCustomPrintToggle,
  hasPurchasedSubscription,
  hasSubscriptionInCart,
  subscriptionAvailable,
  albumMembershipGroup,
  disabled,
  disabledElement,
  hideActions,
  isAllPhotos,
  watermarkSettings,
  teamPhotos,
  photo,
}) => {
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-album-custom-print': true,
  });
  const [configurationModalOpen, setConfigurationModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const openModal = useOpenModal();
  const name = getAlbumCustomPrintName(albumCustomPrint);
  const price = getAlbumCustomPrintPrice(albumCustomPrint);
  const discountPrice = getAlbumCustomPrintPrice(albumCustomPrint, true);
  const previewImage = albumCustomPrint.custom_print.preview_image;
  const shouldDiscount = hasSubscriptionInCart;
  const currencySymbol = getCurrencySymbol(
    albumCustomPrint?.discount_price?.currency ??
      albumCustomPrint?.price?.currency ??
      CURRENCY.USD,
  );

  const onAlbumCustomPrintRequest = () => {
    if (disabled) {
      openModal(REQUIRES_PRINT_PACKAGE_ALERT_MODAL, { name });
      return;
    }

    setConfigurationModalOpen(true);
  };

  return (
    <div className={classes}>
      <div>
        <div className="purchasing-option-item-info-extra">
          <Badge
            badgeStyle="yellow"
            className="purchasing-option-item-info-badge"
          >
            <PriceDisplay
              title="Custom print price"
              price={shouldDiscount && !!discountPrice ? discountPrice : price}
              currency={
                albumCustomPrint?.discount_price?.currency ??
                albumCustomPrint?.price?.currency ??
                CURRENCY.USD
              }
              oldPrice={price}
              showDiscount={shouldDiscount}
              animatedStrikethrough
              trimDecimalZeros={false}
            />
          </Badge>
        </div>

        <div className="purchasing-option-item-thumb">
          {previewImage ? (
            <img width={75} src={previewImage.url} alt="" />
          ) : (
            <img width={75} src={getAsset('/plaque.svg')} alt="" />
          )}
        </div>
      </div>

      <div className="purchasing-option-item-content-wrap">
        <div className="purchasing-option-item-title">{name}</div>

        <div className="purchasing-option-item-content mb-2">
          <p className="text-sm lh-normal text-dark text-bold">
            Pick your fav pics and build your own{' '}
            {albumCustomPrint.custom_print.name}!
          </p>

          {previewImage && (
            <p className="mb-0">
              <Button
                btnStyle="link"
                className="text-sm lh-normal text-extra-bold"
                onClick={() => setPreviewModalOpen(true)}
              >
                See sample
              </Button>
            </p>
          )}

          {discountPrice > 0 && subscriptionAvailable && (
            <p className="d-block text-sm text-italic text-bold">
              Only{' '}
              <span className="text-primary">
                {currencySymbol}
                {formatPriceForDisplay(discountPrice, true)}
              </span>{' '}
              if purchased with a Digital Photo Pass. Discount is limited to 1
              print.
            </p>
          )}

          {disabled && disabledElement && !readOnly && <>{disabledElement}</>}
        </div>

        {!readOnly && !hideActions && (
          <div className="d-flex align-items-md-center flex-column flex-md-row">
            <Button
              btnStyle="primary"
              className={classNames({
                'purchasing-options-item-btn': true,
                'mr-auto': true,
                'mr-md-1': !!cartItem,
              })}
              onClick={onAlbumCustomPrintRequest}
              data-testid="btn-album-custom-print"
            >
              {cartItem ? 'Configure another' : 'Configure'}
            </Button>

            {cartItem && (
              <div className="pos-rel">
                <p className="in-cart-label">
                  <AddedToCartLabel label="In cart" />
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <AccountCardAlbumCustomPrintConfiguratorModal
        forceOpen={configurationModalOpen}
        albumCustomPrint={albumCustomPrint}
        onClose={() => setConfigurationModalOpen(false)}
        onFinished={({ accountCard }) => {
          onAlbumCustomPrintToggle({ accountCard });
          setConfigurationModalOpen(false);
        }}
        deleteAccountCardOnDismiss
        identity={identity}
        albumMembershipGroup={albumMembershipGroup}
        isAllPhotos={isAllPhotos}
        watermarkSettings={watermarkSettings}
        teamPhotos={teamPhotos}
        photo={photo}
      />

      {previewImage && (
        <Fragment>
          <AlbumCustomPrintPreviewModal
            forceOpen={previewModalOpen}
            onClose={() => setPreviewModalOpen(false)}
            albumCustomPrint={albumCustomPrint}
            onAlbumCustomPrintToggle={onAlbumCustomPrintRequest}
            shouldDiscount={shouldDiscount}
            albumCustomPrintInCart={!!cartItem}
            hasPurchasedSubscription={hasPurchasedSubscription}
            disableSelection={disabled}
          />
          <ImagePreload
            urls={[albumCustomPrint.custom_print.preview_image.url]}
          />
        </Fragment>
      )}
    </div>
  );
};

AccountCardAlbumCustomPrintOption.propTypes = propTypes;

export default AccountCardAlbumCustomPrintOption;
